import { useCallerLookup } from "@/lib/hooks/use-caller-lookup";
import { useStaffCallerLookup } from "@/lib/hooks/use-staff-caller-lookup";
import { parseCaller } from "@/lib/twilio/caller";
import type { ActiveCall } from "@/lib/twilio/voice-context";
import { getGuestDisplayName, getStaffDisplayName } from "@/lib/utils";

/**
 * Who a call is with: an answered call's caller, or whom the host dialled,
 * parsed the same way so a teammate's `client:` identity is never shown as a
 * number. An incoming call is matched as its ringing screen matched it,
 * among the guests of the property it rang for. An outgoing call can reach
 * any property's guest, so its number is matched across all of them.
 */
export function useCallCounterparty(activeCall: ActiveCall | null) {
  const party =
    activeCall?.caller ??
    (activeCall?.to ? parseCaller({ from: activeCall.to }) : undefined);
  const lookup = useCallerLookup(party?.e164, party?.propertyId ?? null);
  const { staff } = useStaffCallerLookup(party?.staffMemberId);
  // A teammate's synced profile wins; until it syncs, the name the call carried.
  const staffName = (staff && getStaffDisplayName(staff)) || party?.staffName;
  // The person the host dialled, never another guest who shares the number:
  // the matched guest's stay shows only when it is the one dialled.
  const dialledName =
    activeCall?.direction === "outgoing" ? activeCall.displayName : undefined;
  const guestName = lookup.guest && getGuestDisplayName(lookup.guest);
  const stay =
    dialledName === undefined || guestName === dialledName
      ? lookup
      : { guest: null, reservation: null, unitCode: null };
  return {
    ...stay,
    avatar: staff?.image ?? undefined,
    name: dialledName ?? guestName ?? staffName,
    number: party?.displayNumber ?? null,
  };
}
